import { useState } from 'react';
import { XSign } from 'assets/images/components/NudosComponents';
import { getNudosData } from '../../../../../../../utils/getNudosData';
import SelectableToolCard from '../../../../../../../components/SelectableToolCard/SelectableToolCard';
import NudosDatePicker from 'components/NudosComponents/NudosDatePicker/NudosDatePicker';
import useDeliveredToolsState, { SECTIONS_STEPS } from '../../../../../../../state/useDeliveredToolsState';
import {
  BordOneToneIcon,
  NudosButton,
  NudosStaticAlertInComponent
} from '../../../../../../../components/NudosComponents';
import { IlogisticServiceDetails } from '../../../../../../../types/services';
import { updateDeliveryStatus, updateStatusService } from '../../../../../../../services/services';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../../../utils/displayNudosStandardNotifications';
import {
  extractUndeliveredProductIds,
  buildMarkAsDeliveredPayload
} from '../../../../../../../utils/logisticServiceOriginDestination.utils';

import './ConfirmationSection.scss';

interface ConfirmationSectionProps {
  closeModalCallback: () => void;
  serviceDetails: IlogisticServiceDetails;
  closeModalAfterSubmitCallback: () => void;
  isUpdateTools?: boolean;
}

const ConfirmationSection = ({
  closeModalCallback,
  serviceDetails,
  closeModalAfterSubmitCallback,
  isUpdateTools = false
}: ConfirmationSectionProps) => {
  const {
    deliveryDate,
    setCurrentStep,
    setDeliveryDate,
    getUnselectedToolsArray,
    getSelectedToolsArray,
    markedNotDeliveredToolIds
  } = useDeliveredToolsState();

  const [isProcessing, setIsProcessing] = useState(false);

  const selectedTools = getSelectedToolsArray();
  const unselectedTools = getUnselectedToolsArray();

  const handleUpdateUndeliveredTools = async (): Promise<void> => {
    if (!serviceDetails?.id) {
      displayErrorNotification({
        customJSXMessage: <>Error: Faltan datos del servicio logístico</>
      });
      return;
    }

    const undeliveredProductIds = extractUndeliveredProductIds(unselectedTools);

    try {
      setIsProcessing(true);
      await updateDeliveryStatus({
        logisticServiceId: serviceDetails.id,
        notDeliveredProductIds: undeliveredProductIds
      });

      displaySuccessNotification({
        customJSXMessage: <>Herramientas actualizadas exitosamente</>
      });

      closeModalAfterSubmitCallback();
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al actualizar el estado de entrega de las herramientas</>
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMarkServiceAsDelivered = async (): Promise<void> => {
    if (!serviceDetails?.id) {
      displayErrorNotification({
        customJSXMessage: <>Error: Faltan datos del servicio logístico</>
      });
      return;
    }

    if (!deliveryDate) {
      displayErrorNotification({
        customJSXMessage: <>Debes seleccionar una fecha de entrega</>
      });
      return;
    }

    const nudosUser = getNudosData();
    if (!nudosUser?.userData?.id) {
      displayErrorNotification({
        customJSXMessage: <>Error: No se pudo obtener la información del usuario</>
      });
      return;
    }

    const undeliveredProductIds = extractUndeliveredProductIds(unselectedTools);
    const statusUpdatePayload = buildMarkAsDeliveredPayload(nudosUser.userData.id, deliveryDate, [
      ...undeliveredProductIds,
      ...markedNotDeliveredToolIds
    ]);

    try {
      setIsProcessing(true);
      await updateStatusService(serviceDetails.id, statusUpdatePayload);

      displaySuccessNotification({
        customJSXMessage: <>Servicio logístico actualizado exitosamente</>
      });

      closeModalAfterSubmitCallback();
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al actualizar el estado del servicio logístico</>
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirm = async (): Promise<void> => {
    if (isUpdateTools) {
      handleUpdateUndeliveredTools();
    } else {
      handleMarkServiceAsDelivered();
    }
  };

  const handleBack = () => {
    setCurrentStep(SECTIONS_STEPS.SELECTION);
  };

  const getSectionContent = (isUpdateTools: boolean) => {
    if (isUpdateTools) {
      return {
        unselectedToolsTitle: `Herramientas que no serán entregadas`,
        unselectedToolsAlert: `Estas herramientas <strong>no serán entregadas</strong> y permanecerán en su ubicación anterior`,
        selectedToolsTitle: `Herramientas que serán entregadas`,
        nextButton: {
          text: 'Actualizar herramientas',
          action: () => handleConfirm(),
          disabledCondition: () => isProcessing,
          isLoading: isProcessing
        },
        backButton: {
          text: 'Regresar',
          action: () => handleBack()
        }
      };
    }

    return {
      unselectedToolsTitle: `Herramientas que no fueron entregadas`,
      unselectedToolsAlert: `Estas herramientas <strong>no fueron entregadas</strong> y permanecerán en su ubicación anterior`,
      selectedToolsTitle: `Herramientas que fueron entregadas`,
      nextButton: {
        text: 'Pasar a entregado',
        action: () => handleConfirm(),
        disabledCondition: () => !deliveryDate || isProcessing,
        isLoading: isProcessing
      },
      backButton: {
        text: 'Regresar',
        action: () => handleBack()
      }
    };
  };

  const sectionContent = getSectionContent(isUpdateTools);

  return (
    <div className="confirmationSection">
      <div className="exitButton" onClick={closeModalCallback}>
        <XSign fill={'#B3B3B3'} />
      </div>

      <div className="header">
        <div className="iconContainer">
          <BordOneToneIcon variant="triangleAlertCircle" standardSize={32} />
        </div>
        <h1 className="title">Revisa y confirma las herramientas antes de continuar</h1>
      </div>

      {!isUpdateTools && (
        <div className="datePickerContainer">
          <NudosDatePicker
            componentSize="small"
            label="Fecha de entrega (obligatorio)"
            updateCurrentValueCallback={setDeliveryDate}
            isFilled={!!deliveryDate}
            defaultValue={deliveryDate}
            desiredTimeZone={-6}
          />
        </div>
      )}

      <div className="toolsContainer" style={{ height: isUpdateTools ? '450px' : '385px' }}>
        {unselectedTools?.length > 0 && (
          <>
            <div className="unselectedToolsHeader">
              <BordOneToneIcon variant="xCircle" standardSize={8} stroke="#FF0000" />
              <span className="unselectedToolsHeaderText">{sectionContent.unselectedToolsTitle}</span>
            </div>

            <NudosStaticAlertInComponent
              type="warning"
              color="yellow"
              alertOptions={{
                title: (
                  <span
                    className="unselectedToolsAlert"
                    dangerouslySetInnerHTML={{ __html: sectionContent.unselectedToolsAlert }}
                  />
                )
              }}
            />

            {unselectedTools?.map(({ tool, originalIndex }) => (
              <SelectableToolCard key={`unselected-${originalIndex}`} tool={tool} customClassName="borderRed" />
            ))}
          </>
        )}

        {selectedTools?.length > 0 && unselectedTools?.length > 0 && <div className="toolsDivider" />}

        {selectedTools?.length > 0 && (
          <div className="selectedToolsHeader">
            <span className="selectedToolsHeaderText">{sectionContent.selectedToolsTitle}</span>
          </div>
        )}

        {selectedTools?.map(({ tool, originalIndex }) => (
          <SelectableToolCard key={`selected-${originalIndex}`} customClassName="borderGray" tool={tool} isSelected />
        ))}
      </div>

      <div className="confirmationButtons">
        <NudosButton
          componentSize="small"
          colorPalette="secondary"
          buttonText={sectionContent.backButton.text}
          handleClick={sectionContent.backButton.action}
        />

        <NudosButton
          componentSize="small"
          buttonText={sectionContent.nextButton.text}
          handleClick={sectionContent.nextButton.action}
          isButtonDisabled={sectionContent.nextButton.disabledCondition()}
          isButtonLoading={sectionContent.nextButton.isLoading}
        />
      </div>
    </div>
  );
};

export default ConfirmationSection;
